import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import useAuth from '../hooks/useAuth';
import {
  submitReview,
  getAssessmentForReview,
  getTemplate,
  claimAssessment,
  releaseAssessment,
} from '../services/mentorApi';
import { formatDate } from '../utils/formatters';

// Evaluates critical rule client-side to match the intern-side highlight.
function isCritical(question, value) {
  const rule = question.critical_if;
  if (!rule || value == null || value === '') return false;
  if (rule.yes === true && value === 'yes') return true;
  if (rule.no === true && value === 'no') return true;
  if (typeof rule.rating_lte === 'number' && Number(value) <= rule.rating_lte) return true;
  if (Array.isArray(rule.includes) && Array.isArray(value)) {
    return value.some((v) => rule.includes.includes(v));
  }
  return false;
}

function formatValue(v) {
  if (v == null || v === '') return '(not answered)';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'string' && v.startsWith('http')) return null; // photo — rendered separately
  return String(v);
}

export default function ReviewDetailPage() {
  const { assessmentId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [assessment, setAssessment] = useState(null);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [decision, setDecision] = useState('');
  const [feedback, setFeedback] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    (async () => {
      try {
        const { assessment: a } = await getAssessmentForReview(assessmentId);
        setAssessment(a);
        const ad = a?.assessment_data || {};
        if (ad.template_id && ad.template_version) {
          const { template: t } = await getTemplate(ad.template_id, ad.template_version);
          setTemplate(t);
        }
      } catch (err) {
        setLoadError(err?.response?.data?.error || err.message || 'Failed to load assessment');
      } finally {
        setLoading(false);
      }
    })();
  }, [assessmentId]);

  const reloadAssessment = async () => {
    const { assessment: a } = await getAssessmentForReview(assessmentId);
    setAssessment(a);
    return a;
  };

  const handleClaim = async () => {
    setClaiming(true);
    try {
      await claimAssessment(assessmentId, user.mentor_id);
      await reloadAssessment();
      setSnack({ open: true, message: 'Claimed. You have 24 hours to complete the review.', severity: 'success' });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        await reloadAssessment();
        setSnack({ open: true, message: 'Another mentor claimed it first.', severity: 'warning' });
      } else {
        setSnack({ open: true, message: err?.response?.data?.error || err.message || 'Claim failed', severity: 'error' });
      }
    } finally {
      setClaiming(false);
    }
  };

  const handleRelease = async () => {
    const reason = window.prompt('Optional: why are you releasing this back to the pool? (for audit — not shown to intern)') || '';
    if (reason === null) return;
    setReleasing(true);
    try {
      await releaseAssessment(assessmentId, user.mentor_id, reason);
      setSnack({ open: true, message: 'Released back to pool.', severity: 'success' });
      setTimeout(() => navigate('/queue'), 1200);
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.error || err.message || 'Release failed', severity: 'error' });
    } finally {
      setReleasing(false);
    }
  };

  const handleSubmit = async () => {
    if (!decision || !feedback.trim() || !confirmed) return;
    setSubmitting(true);
    try {
      await submitReview({
        assessmentId,
        mentorId: user.mentor_id,
        decision,
        feedback: feedback.trim(),
      });
      setSnack({
        open: true,
        message: decision === 'approved'
          ? 'Approved. Report is being delivered to the client.'
          : 'Sent back to intern for revision.',
        severity: 'success',
      });
      setTimeout(() => navigate('/queue'), 1500);
    } catch (err) {
      setSnack({
        open: true,
        message: err?.response?.data?.error || err.message || 'Failed to submit review',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (loadError) {
    return <Alert severity="error">{loadError}</Alert>;
  }

  const assessmentData = assessment?.assessment_data || {};
  const responses = assessmentData.responses || {};
  const criticalFindings = assessmentData.critical_findings || [];

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/queue')} sx={{ mb: 2 }}>
        Back to queue
      </Button>

      <Typography variant="h4" sx={{ mb: 0.5 }}>Review Assessment</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: 'monospace' }}>
        {assessmentId}
      </Typography>

      {/* Metadata */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <MetaField label="Status" value={assessment?.status} />
          <MetaField label="Intern" value={assessment?.intern_id?.slice(0, 8) + '…'} />
          <MetaField label="Scheduled" value={formatDate(assessment?.scheduled_time)} />
          <MetaField label="Submitted" value={formatDate(assessment?.submitted_at)} />
          {template && <MetaField label="Template" value={`${template.template_id} v${template.version}`} />}
        </Box>
      </Paper>

      {/* Critical findings summary */}
      {criticalFindings.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, borderLeft: '4px solid', borderColor: 'error.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <WarningAmberIcon color="error" />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {criticalFindings.length} Critical Finding{criticalFindings.length === 1 ? '' : 's'}
            </Typography>
          </Box>
          {criticalFindings.map((cf) => (
            <Box key={cf.question_id} sx={{ pl: 3, py: 0.5 }}>
              <Typography variant="body2">
                <b>{cf.number}</b> {cf.prompt}
              </Typography>
              <Typography variant="caption" color="error" sx={{ fontWeight: 600 }}>
                Intern answered: {Array.isArray(cf.value) ? cf.value.join(', ') : cf.value}
                {cf.notes && <Box component="span" sx={{ fontStyle: 'italic', ml: 1 }}>— {cf.notes}</Box>}
              </Typography>
            </Box>
          ))}
        </Paper>
      )}

      {/* Responses by category */}
      {template ? (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Intern's Responses</Typography>
          {template.categories.map((cat) => {
            const catCriticals = cat.questions.filter((q) => {
              const r = responses[q.id];
              return isCritical(q, r?.value);
            }).length;
            return (
              <Accordion key={cat.id} defaultExpanded={catCriticals > 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography sx={{ fontWeight: 600, flex: 1 }}>{cat.title}</Typography>
                    {catCriticals > 0 && (
                      <Chip
                        label={`${catCriticals} critical`}
                        size="small"
                        color="error"
                        icon={<WarningAmberIcon />}
                      />
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {cat.questions.length} Q
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {cat.questions.map((q) => {
                    const r = responses[q.id];
                    const critical = isCritical(q, r?.value);
                    const v = r?.value;
                    const formattedValue = formatValue(v);
                    const isPhoto = typeof v === 'string' && v.startsWith('http');
                    return (
                      <Box
                        key={q.id}
                        sx={{
                          py: 1,
                          pl: 1.5,
                          borderLeft: critical ? '3px solid' : '1px solid',
                          borderColor: critical ? 'error.main' : 'divider',
                          mb: 1,
                          backgroundColor: critical ? '#fff5f5' : 'transparent',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {q.number}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {q.prompt}
                        </Typography>
                        {isPhoto ? (
                          <Box sx={{ mt: 0.5 }}>
                            <Box
                              component="img"
                              src={v}
                              alt="evidence"
                              sx={{
                                maxWidth: 240,
                                maxHeight: 180,
                                objectFit: 'cover',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                              }}
                            />
                          </Box>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 0.25,
                              color: v == null || v === '' ? 'text.disabled' : 'text.primary',
                              fontWeight: v == null || v === '' ? 400 : 500,
                            }}
                          >
                            → {formattedValue}
                          </Typography>
                        )}
                        {r?.notes && (
                          <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary', display: 'block', mt: 0.5 }}>
                            Notes: {r.notes}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          Template metadata missing from this assessment — showing raw responses only.
        </Alert>
      )}

      {/* Claim state banner */}
      {(() => {
        const status = assessment?.status;
        const expertId = assessment?.expert_id;
        const mentorId = user?.mentor_id;
        const slaExpiresAt = assessment?.sla_expires_at;
        const slaHoursLeft = slaExpiresAt
          ? (slaExpiresAt * 1000 - Date.now()) / 3600000
          : null;

        if (status === 'pending_review') {
          return (
            <Paper sx={{ p: 3, mb: 3, borderLeft: '4px solid', borderColor: 'warning.main' }}>
              <Typography variant="h6" sx={{ mb: 1 }}>This assessment is unclaimed</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Click <b>Claim</b> to lock it for review. You&apos;ll have 24 hours from the claim time to submit your decision.
                First-click-wins — if another mentor claims it while you&apos;re reading, you&apos;ll see a notice.
              </Typography>
              <Button
                variant="contained"
                color="success"
                startIcon={<LockOpenIcon />}
                onClick={handleClaim}
                disabled={claiming}
                size="large"
              >
                {claiming ? <CircularProgress size={20} color="inherit" /> : 'Claim — Start Review'}
              </Button>
            </Paper>
          );
        }

        if (status === 'under_review' && expertId && expertId !== mentorId) {
          return (
            <Alert severity="info" sx={{ mb: 3 }}>
              This assessment is currently claimed by another mentor ({expertId.slice(0, 8)}…).
              You can view it but can&apos;t submit a review unless it&apos;s released or auto-expires.
            </Alert>
          );
        }

        if (status === 'under_review' && expertId === mentorId) {
          return (
            <Paper sx={{ p: 2, mb: 3, borderLeft: '4px solid', borderColor: slaHoursLeft != null && slaHoursLeft < 4 ? 'error.main' : 'primary.main' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTimeIcon fontSize="small" color={slaHoursLeft != null && slaHoursLeft < 4 ? 'error' : 'primary'} />
                  <Typography variant="body2">
                    <b>Claimed by you.</b>{' '}
                    {slaHoursLeft != null
                      ? slaHoursLeft > 0
                        ? `${slaHoursLeft.toFixed(1)}h until 24h SLA.`
                        : 'SLA expired — will auto-release on next cron run.'
                      : ''}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={handleRelease}
                  disabled={releasing}
                >
                  {releasing ? <CircularProgress size={16} /> : 'Release back to pool'}
                </Button>
              </Box>
            </Paper>
          );
        }
        return null;
      })()}

      {/* Decision panel — only rendered when mentor owns an active claim */}
      {assessment?.status === 'under_review' && assessment?.expert_id === user?.mentor_id && (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Decision</Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant={decision === 'approved' ? 'contained' : 'outlined'}
            color="success"
            startIcon={<CheckIcon />}
            onClick={() => setDecision('approved')}
            size="large"
          >
            Approve — Deliver to Client
          </Button>
          <Button
            variant={decision === 'rejected' ? 'contained' : 'outlined'}
            color="error"
            startIcon={<CloseIcon />}
            onClick={() => setDecision('rejected')}
            size="large"
          >
            Request Revision
          </Button>
        </Box>

        <TextField
          label="Feedback (required)"
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          fullWidth
          multiline
          rows={6}
          placeholder={
            decision === 'approved'
              ? 'What did the intern do well? Any notes you want the client to know about?'
              : decision === 'rejected'
                ? 'What needs to be revised? Be specific — the intern will see this.'
                : 'Pick a decision above, then write your feedback.'
          }
          sx={{ mb: 2 }}
        />

        {decision && feedback.trim() && (
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
            }
            label={
              decision === 'approved'
                ? 'I confirm this report meets CWC quality standards and is ready to deliver to the client.'
                : 'I confirm the intern should revise and resubmit this assessment.'
            }
            sx={{ mb: 2 }}
          />
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color={decision === 'approved' ? 'success' : 'error'}
            onClick={handleSubmit}
            disabled={!decision || !feedback.trim() || !confirmed || submitting}
            size="large"
          >
            {submitting
              ? <CircularProgress size={20} color="inherit" />
              : decision === 'approved'
                ? 'Approve & Deliver'
                : decision === 'rejected'
                  ? 'Send Back for Revision'
                  : 'Submit Review'}
          </Button>
        </Box>
      </Paper>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function MetaField({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>{value ?? '—'}</Typography>
    </Box>
  );
}
